import _, { Dictionary } from 'lodash';
import Vue from 'vue';
import {mapActions, mapState, mapGetters} from 'vuex';
import EventHub from '../../lib/eventHub';
import {AxiosResponse} from 'axios';
import {AlertNotificationModuleConstant, EmployeeProfileModuleConstant, EventConstant, UrlModuleConstant} from '../../lib/constants';
import ClubService from '../../lib/services/clubService';
import BioRoleService from '../../lib/services/bioRoleService';
import BioRoleMuleService from '../../lib/services/bioRoleMuleService';
import BioSearchService from '../../lib/services/bioSearchService';
import BioSearchMuleService from '../../lib/services/bioSearchMuleService';
import BioSearch from './bio-search.html';
import BioAdmin from 'types/ba/bioAdmin';
import LtFramework from 'types/lt/ltFramework';
import { AlertNotificationType } from 'src/lib/enums';

interface IComponentData {
	bioRoleServiceLoading: boolean;
	bioSearchServiceLoading: boolean;
	searchString: string;
	minSearchLength: number;
	clubsByCountry: BioAdmin.ICountry[];
	selectedClub: number;
	roles: BioAdmin.IRole[];
	role: string;
	results: BioAdmin.ITeamMemberListProfile[];
	searchExecuted: boolean;
	useSpringApi: boolean;
}

const clubService: BioAdmin.IClubService = new ClubService();
const bioRoleService: BioAdmin.IBioRoleService = new BioRoleService();
const bioRoleMuleService: BioAdmin.IBioRoleService = new BioRoleMuleService();
const bioSearchService: BioAdmin.IBioSearchService = new BioSearchService();
const bioSearchMuleService: BioAdmin.IBioSearchService = new BioSearchMuleService();

export default Vue.component('bioSearch', {
	template: BioSearch,
	data(): IComponentData {
		return {
			bioRoleServiceLoading: true,
			bioSearchServiceLoading: false,
			searchString: '',
			minSearchLength: 3,
			clubsByCountry: [],
			selectedClub: 0,
			roles: [],
			role: '',
			results: [],
			searchExecuted: false,
			useSpringApi: false,
		};
	},
	computed: {
		isValidSearch(): boolean {
			const len = this.searchString.length;
			const min = this.minSearchLength;
			if (len >= min || (this.selectedClub.toString() !== '0' && (len === 0 || len >= min )) ) {
				return true;
			} else {
				return false;
			}
		},
	},
	methods: {
		...mapActions(AlertNotificationModuleConstant.name, {
			synchronizeAlertNotificationStore: AlertNotificationModuleConstant.action.synchronize,
			openAlertNotification: AlertNotificationModuleConstant.action.open,
		}),
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
		getRoles() {
			if (this.useSpringApi)
				return bioRoleService.getRoleList().then((response: AxiosResponse<BioAdmin.IRole[]>) => {
				this.roles = _.filter(response.data, item => item.name !== 'Dynamic Personal Trainer');
				}).catch((error) => {
					this.showErrorAlert('Error Loading Role Options');
				});
			else
				return bioRoleMuleService.getRoleList().then((response: AxiosResponse<BioAdmin.IRole[]>) => {
					this.roles = _.filter(response.data, item => item.name !== 'Dynamic Personal Trainer');
					}).catch((error) => {
						this.showErrorAlert('Error Loading Role Options');
					});
		},
		getAllClubs() {
			return clubService.getClubLocations()
				.then((response: AxiosResponse<BioAdmin.IClubsResponse>) => {
					this.setAllClubs(response.data);
				}).catch((error) => {
					this.showErrorAlert('Error Loading Club Data');
				});
		},
		getBioSearch() {
			if (this.useSpringApi)
				return bioSearchService.getBioSearch(this.searchString, this.selectedClub, this.role).then((response: AxiosResponse<BioAdmin.ITeamMemberListProfile[]>) => {
					this.results = response.data;
				}).catch((error) => {
					this.showErrorAlert('Error Searching Bios');
				});
			else
				return bioSearchMuleService.getBioSearch(this.searchString, this.selectedClub, this.role).then((response: AxiosResponse<BioAdmin.ITeamMemberListProfile[]>) => {
					this.results = response.data;
				}).catch((error) => {
					this.showErrorAlert('Error Searching Bios');
				});
		},
		executeSearch() {
			if(this.isValidSearch) {
				this.results =[];
				this.getBioSearch();
				this.searchExecuted = true;
			}
		},
		showErrorAlert(title: string) {
			const alertNotification: LtFramework.IAlertNotification = {
				id: '1',
				type: AlertNotificationType.Danger,
				message: 'An unexpected error has occurred. Please logout, log back in and try again. If the problem persists please create a ticket with IT Support.',
				dismissible: false,
				title: title,
			};
			this.openAlertNotification(alertNotification);
			location.hash = '#alert-notification-' + alertNotification.type;
		},
		setAllClubs(clubs: any) {
			const countries: BioAdmin.ICountry[] = [];
			// group the list of clubs by country
			const countryClubs: Dictionary<BioAdmin.IClub[]> = _.groupBy(clubs, 'country');
			_.forEach(countryClubs, (cValue, cKey) => {
				const stateProvinces: BioAdmin.IStateProvince[] = [];
				// group each country into states
				const stateClubs: Dictionary<BioAdmin.IClub[]> = _.groupBy(cValue, 'state');
				_.forEach(stateClubs, (sValue, sKey) => {
					stateProvinces.push({
						name: sKey,
						clubs: _.orderBy(_.filter(sValue, obj => obj.clubTypeId === 1 || obj.clubTypeId === 2), 'clubTitle'),
					});
				});
				countries.push({
					name: cKey,
					stateProvinces: _.orderBy(stateProvinces, 'name'),
				});
			});
			this.clubsByCountry = countries;
		},
	},
	created(): void {
		$('main').css('background-color', '#FFF');
		EventHub.$on(EventConstant.BioRoleServiceLoading, (loading: boolean) => {
			this.bioRoleServiceLoading = loading;
		});
		EventHub.$on(EventConstant.BioSearchServiceLoading, (loading: boolean) => {
			this.bioSearchServiceLoading = loading;
		});
		Promise.all([this.synchronizeAlertNotificationStore(), this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore()]).then(() => {
			const featureFlags = window.lt.features;
			if(featureFlags) {
				const feature = featureFlags[featureFlags.findIndex( (x: any) => x.featureId === 'biosSpringApi')];
				if(feature && feature.enabled)
					this.useSpringApi = true;
			}
			this.getAllClubs();
			this.getRoles();
		});
	},
});
